import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import VendorApprovalBadge from "@/components/VendorApprovalBadge";
import { Check, X } from "lucide-react";

/**
 * The admin-managed catalog products (db.deals) linked to the logged-in
 * broker's vendor. Pending ones can be approved or rejected here.
 */
export default function BrokerDealsTable() {
    const [deals, setDeals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [rejecting, setRejecting] = useState(null);
    const [reason, setReason] = useState("");

    /** Send the vendor's approve/reject decision and update the row. */
    const decide = async (id, decision, rejectReason) => {
        setBusy(true);
        try {
            const { data: updated } = await api.post(
                `/broker/deals/${id}/approval`,
                { decision, reason: rejectReason || null }
            );
            setDeals((ds) => ds.map((d) => (d.id === id ? updated : d)));
            setRejecting(null);
            toast.success(decision === "approved" ? "Product approved" : "Product rejected");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        api
            .get("/broker/deals")
            .then((r) => setDeals(r.data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, []);

    if (!loading && deals.length === 0) {
        return (
            <div className="rounded-2xl bg-white border border-navy-100/70 py-16 text-center">
                <img
                    src="/panda/panda-checklist.png"
                    alt=""
                    className="h-32 mx-auto mb-4"
                />
                <div className="font-display text-2xl text-navy-700 mb-1">
                    No linked products yet
                </div>
                <div className="text-navy-500 text-sm">
                    Perx hasn't linked any catalogue products to your vendor.
                </div>
            </div>
        );
    }

    return (
        <div className="rounded-2xl bg-white border border-navy-100/70 shadow-soft overflow-hidden">
            <Table data-testid="broker-deals-table">
                <TableHeader>
                    <TableRow className="bg-cream hover:bg-cream">
                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                            Product
                        </TableHead>
                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                            Member price
                        </TableHead>
                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                            Savings
                        </TableHead>
                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                            Status
                        </TableHead>
                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                            Your approval
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {deals.map((d) => {
                        const symbol = d.is_paid_USD ? "US$" : "$";
                        const active = d.status === "active";
                        return (
                            <TableRow key={d.id} data-testid={`broker-deal-row-${d.id}`}>
                                <TableCell className="font-medium text-navy-700">
                                    <Link
                                        to={`/broker/products/${d.id}/preview`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`broker-deal-preview-${d.id}`}
                                        className="hover:text-gold-600 underline-offset-2 hover:underline"
                                    >
                                        {d.title}
                                    </Link>
                                </TableCell>
                                <TableCell className="text-sm">
                                    {symbol}
                                    {d.price?.toFixed(2)}
                                    {d.original_price > d.price && (
                                        <span className="text-navy-400 line-through ml-2">
                                            {symbol}
                                            {d.original_price?.toFixed(2)}
                                        </span>
                                    )}
                                </TableCell>
                                <TableCell className="text-gold-600 font-medium">
                                    {d.savings_pct}%
                                </TableCell>
                                <TableCell>
                                    <Badge
                                        className={
                                            (active
                                                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                                : "bg-navy-50 text-navy-500 border-navy-100") +
                                            " border text-[10px] uppercase tracking-widest"
                                        }
                                    >
                                        {active ? "Active" : "Inactive"}
                                    </Badge>
                                </TableCell>
                                <TableCell>
                                    {d.vendor_approval === "pending" ? (
                                        <div className="inline-flex items-center gap-2">
                                            <Button
                                                size="sm"
                                                disabled={busy}
                                                onClick={() => decide(d.id, "approved")}
                                                data-testid={`broker-deal-approve-${d.id}`}
                                                className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white h-8"
                                            >
                                                <Check size={14} className="mr-1.5" /> Approve
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                disabled={busy}
                                                onClick={() => {
                                                    setReason("");
                                                    setRejecting(d);
                                                }}
                                                data-testid={`broker-deal-reject-${d.id}`}
                                                className="rounded-full border-red-200 text-red-600 hover:bg-red-50 h-8"
                                            >
                                                <X size={14} className="mr-1.5" /> Reject
                                            </Button>
                                        </div>
                                    ) : (
                                        <VendorApprovalBadge
                                            testid={`broker-deal-approval-${d.id}`}
                                            status={d.vendor_approval}
                                        />
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>

            <Dialog open={!!rejecting} onOpenChange={(o) => !o && setRejecting(null)}>
                <DialogContent className="bg-white" data-testid="broker-deal-reject-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl text-navy-700">
                            Reject {rejecting?.title}
                        </DialogTitle>
                        <DialogDescription className="text-navy-500">
                            Tell Perx what needs changing. They'll update it and send it back to you.
                        </DialogDescription>
                    </DialogHeader>
                    <Textarea
                        rows={4}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="e.g. The price is wrong, it should be $49."
                        data-testid="broker-deal-reject-reason"
                        className="bg-white border-navy-100 focus-visible:ring-gold-500"
                    />
                    <DialogFooter>
                        <Button
                            disabled={busy}
                            onClick={() => decide(rejecting.id, "rejected", reason)}
                            data-testid="broker-deal-reject-confirm"
                            className="rounded-full bg-red-600 hover:bg-red-700 text-white h-10 px-6"
                        >
                            Reject product
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
